import React, { useState } from "react";
import axios from "axios";
import { CAREERS } from "@/constants/testIds";
import { ArrowRight, MapPin, Briefcase, X, Upload, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOBS = [
  {
    slug: "logistics-manager",
    title: "Logistics Manager",
    location: "South Florida",
    type: "Full-time",
    summary:
      "Oversee inbound and outbound shipments, vendor coordination, and warehouse flow for premium stone and tile inventory.",
    responsibilities: [
      "Manage daily logistics across South Florida deliveries",
      "Coordinate with suppliers, drivers, and contractors",
      "Maintain inventory accuracy and warehouse organization",
      "Optimize route planning and shipment scheduling",
    ],
  },
  {
    slug: "driver",
    title: "Driver",
    location: "South Florida",
    type: "Full-time",
    summary:
      "Deliver natural stone, tile, and paver orders to job sites and showrooms across the South Florida region.",
    responsibilities: [
      "Safely operate company delivery vehicles",
      "Load and unload heavy stone and tile products",
      "Provide courteous, professional customer interactions",
      "Maintain a clean driving record and vehicle log",
    ],
  },
];

const ApplicationForm = ({ position, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    cover_message: "",
  });
  const [resume, setResume] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please complete name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("position", position);
      fd.append("full_name", form.full_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_message", form.cover_message || "");
      if (resume) fd.append("resume", resume);

      await axios.post(`${API}/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Application received. We'll be in touch shortly.");
      onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't submit your application. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="full_name" className="label-overline">Full Name *</Label>
          <Input
            id="full_name"
            name="full_name"
            data-testid={CAREERS.inputName}
            value={form.full_name}
            onChange={onChange}
            placeholder="Jane Doe"
            className="mt-2 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] h-12"
            required
          />
        </div>
        <div>
          <Label htmlFor="phone" className="label-overline">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            data-testid={CAREERS.inputPhone}
            value={form.phone}
            onChange={onChange}
            placeholder="(305) 555-0100"
            className="mt-2 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] h-12"
            required
          />
        </div>
      </div>
      <div>
        <Label htmlFor="email" className="label-overline">Email *</Label>
        <Input
          id="email"
          type="email"
          name="email"
          data-testid={CAREERS.inputEmail}
          value={form.email}
          onChange={onChange}
          placeholder="jane@example.com"
          className="mt-2 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] h-12"
          required
        />
      </div>
      <div>
        <Label htmlFor="cover_message" className="label-overline">Cover Note (optional)</Label>
        <Textarea
          id="cover_message"
          name="cover_message"
          data-testid={CAREERS.inputMessage}
          value={form.cover_message}
          onChange={onChange}
          rows={4}
          placeholder="Briefly tell us why you're a fit…"
          className="mt-2 rounded-none border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] resize-none"
        />
      </div>
      <div>
        <Label htmlFor="resume" className="label-overline">Resume (PDF, DOCX · max 5MB)</Label>
        <label
          htmlFor="resume"
          className="mt-2 flex items-center justify-between gap-3 border border-dashed border-[#1A1A1A] px-4 py-4 cursor-pointer hover:bg-[#F9F8F6] transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Upload size={16} className="shrink-0" />
            <span className="truncate text-sm text-[#1A1A1A]">
              {resume ? resume.name : "Click to upload your resume"}
            </span>
          </div>
          <span className="label-overline shrink-0">Browse</span>
        </label>
        <input
          id="resume"
          type="file"
          data-testid={CAREERS.inputResume}
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setResume(e.target.files?.[0] || null)}
          className="hidden"
        />
      </div>

      <button
        type="submit"
        data-testid={CAREERS.submitBtn}
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#1A1A1A] text-[#F9F8F6] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#3a3a3a] transition-colors disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Submitting…
          </>
        ) : (
          <>
            Submit Application <ArrowRight size={16} />
          </>
        )}
      </button>
    </form>
  );
};

export const Careers = () => {
  const [activePosition, setActivePosition] = useState(null);

  return (
    <section
      id="careers"
      data-testid={CAREERS.section}
      className="section bg-[#FFFFFF] border-y border-[#E5E0D8]"
    >
      <div className="container-wide">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-16">
          <div className="md:col-span-7">
            <span className="label-overline">Careers · Now Hiring</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1] font-light">
              Join a team that moves
              <br />
              <span className="italic text-[#8C7A6B]">beautiful materials.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-[#5C5C5C] leading-relaxed">
              We’re hiring two roles in South Florida. Apply directly below — every
              application is reviewed by our operations team.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-[#E5E0D8]">
          {JOBS.map((job) => (
            <article
              key={job.slug}
              data-testid={CAREERS.jobCard(job.slug)}
              className="bg-white p-8 md:p-12 flex flex-col"
            >
              <div className="flex items-center gap-3">
                <span className="label-overline">Open Role</span>
              </div>
              <h3 className="font-heading text-3xl sm:text-4xl mt-4 font-light text-[#1A1A1A]">
                {job.title}
              </h3>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5C5C5C]">
                <span className="inline-flex items-center gap-2">
                  <MapPin size={14} /> {job.location}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Briefcase size={14} /> {job.type}
                </span>
              </div>
              <p className="mt-6 text-[#5C5C5C] leading-relaxed">{job.summary}</p>
              <ul className="mt-6 space-y-3 border-t border-[#E5E0D8] pt-6">
                {job.responsibilities.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-[#1A1A1A]">
                    <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#B89F82]" />
                    {r}
                  </li>
                ))}
              </ul>
              <button
                data-testid={CAREERS.applyBtn(job.slug)}
                onClick={() => setActivePosition(job.title)}
                className="mt-8 self-start inline-flex items-center gap-3 px-8 py-4 bg-[#1A1A1A] text-[#F9F8F6] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#3a3a3a] transition-colors"
              >
                Apply Now <ArrowRight size={16} />
              </button>
            </article>
          ))}
        </div>
      </div>

      <Dialog
        open={!!activePosition}
        onOpenChange={(v) => !v && setActivePosition(null)}
      >
        <DialogContent
          data-testid={CAREERS.modal}
          className="max-w-2xl rounded-none border-[#E5E0D8] bg-[#F9F8F6] p-0"
        >
          <div className="p-8 sm:p-10">
            <DialogHeader className="text-left">
              <span className="label-overline">Apply · In Stone Supplies</span>
              <DialogTitle className="font-heading text-3xl sm:text-4xl font-light mt-3 text-[#1A1A1A]">
                {activePosition}
              </DialogTitle>
              <DialogDescription className="text-[#5C5C5C] mt-2">
                Complete the form and our hiring team will respond within 5 business days.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-8">
              {activePosition && (
                <ApplicationForm
                  position={activePosition}
                  onSuccess={() => setActivePosition(null)}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
